import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import { TitlelessCard, Icon, IconDefinitions, SpinnerDefault, DetailRow } from "../../components";
import {  InspectionService, UrlService, UserService } from "../../services";
import { InspectionSessionDetailsModel, PeakSignalModel, PeakSignalUpdateModel } from "../../data/entities";
import { Roles } from "../../models";
import { MetricPill, BreakdownBar, InspectionHeaderCard, InspectionReportCard, LiveGauge, PeakSignalTable } from "./components";
import { displayPct, getOverallStatus, deriveFlwGaugeConfig, getGaugeLevel, worstGaugeLevel } from "./helper";
import { useGaugeConfig } from "./hooks";
import "./scss/CTEyeClientInspectionDetails.scss";

export function CTEyeClientInspectionDetailsPage() {
  const urlParams = useParams();
  const navigate = useNavigate();
  const id = UrlService.getParamValue("id", urlParams) ?? "0";
  const inspectionId = UrlService.getParamValue("inspectionId", urlParams) ?? "0";
  const service = new InspectionService();

  const [inspection, setInspection] = useState<InspectionSessionDetailsModel | null>(null);
  const [loading, setLoading] = useState(true);
  const gaugeConfig = useGaugeConfig(id);

  useEffect(() => {
    loadInspection();
  }, [inspectionId]);

  const loadInspection = async () => {
    setLoading(true);
    try {
      const data = await service.getSessionDetails(parseInt(inspectionId));
      setInspection(data);
    } catch (error) {
      console.error("Error loading inspection:", error);
    } finally {
      setLoading(false);
    }
  };

  const canEditPeaks = UserService.isInAnyRole([Roles.admin, Roles.dataAnalyst]);
  const handlePeakUpdate = async (signal: PeakSignalModel, changes: PeakSignalUpdateModel) => {
    await service.updatePeakSignal(signal.id, changes);
    await loadInspection();
  };
  const handlePeakDelete = async (signal: PeakSignalModel) => {
    await service.deletePeakSignal(signal.id);
    await loadInspection();
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <SpinnerDefault />
        <p className="text-muted mt-2 small">Loading inspection…</p>
      </div>
    );
  }
  
  if (!inspection) {
    return (
      <TitlelessCard>
        <div className="text-center py-5">
          <Icon
            iconName={IconDefinitions.circleExclamation}
            className="text-danger mb-2"
            style={{ fontSize: "1.5rem" }}
          />
          <p className="text-muted mb-0 small">Inspection not found.</p>
          <Button
            variant="link"
            onClick={() => navigate(-1)}
            className="mt-2"
            size="sm"
          >
            <Icon iconName={IconDefinitions.arrowLeft} className="me-1" />
            Go Back
          </Button>
        </div>
      </TitlelessCard>
    );
  }

  const total = (inspection.passedCount ?? 0) + (inspection.warningCount ?? 0) + (inspection.alertCount ?? 0);
  const passedDisplay = displayPct(inspection.passedCount ?? 0, total);
  const warningDisplay = displayPct(inspection.warningCount ?? 0, total);
  const failedDisplay = displayPct(inspection.alertCount ?? 0, total);
  const isEmpty = total === 0;


  const flwConfig = deriveFlwGaugeConfig(gaugeConfig);
  const showFlwGauge = flwConfig != null && inspection.peakFlwDifference != null;
  const flwTotal =
    (inspection.flwPassedCount ?? 0) + (inspection.flwWarningCount ?? 0) + (inspection.flwAlertCount ?? 0);
  // Null FLW counts = inspection predates the feature; hide the deviation breakdown
  const hasFlwCounts =
    inspection.flwPassedCount != null || inspection.flwWarningCount != null || inspection.flwAlertCount != null;
  const isFlwEmpty = flwTotal === 0;
  const flwPassedDisplay = displayPct(inspection.flwPassedCount ?? 0, flwTotal);
  const flwWarningDisplay = displayPct(inspection.flwWarningCount ?? 0, flwTotal);
  const flwFailedDisplay = displayPct(inspection.flwAlertCount ?? 0, flwTotal);
  const gaugeLevel = worstGaugeLevel(
    getGaugeLevel(inspection.peakFlawValue, gaugeConfig),
    getGaugeLevel(inspection.peakFlwDifference, flwConfig),
  );
  const status = getOverallStatus(
    (inspection.alertCount ?? 0) + (inspection.flwAlertCount ?? 0),
    (inspection.warningCount ?? 0) + (inspection.flwWarningCount ?? 0),
    total + flwTotal,
    gaugeLevel,
  );

  return (
    <div className="row justify-content-center">
      <div className="col-12 col-xl-10">
        {/* Header*/}
        <InspectionHeaderCard
          inspection={inspection}
          inspectionId={inspectionId}
          status={status}
        />

        <div className="row g-4">
          {/* Breakdown + Gauge */}
          <div className="col-lg-6">
            <TitlelessCard className="p-3 h-100">
              <div className="d-flex flex-column h-100 gap-4">
                {/* Pass-rate breakdown */}
                <div>
                  <div className="section-label">
                    Defect Rate Breakdown
                  </div>

                  <BreakdownBar
                    passed={inspection.passedCount ?? 0}
                    warning={inspection.warningCount ?? 0}
                    failed={inspection.alertCount ?? 0}
                    isEmpty={isEmpty}
                  />

                  <div className="d-flex gap-2 mt-3">
                    <MetricPill
                      value={passedDisplay}
                      label="Passed"
                      variant="success"
                      icon={IconDefinitions.circleCheck}
                      isEmpty={isEmpty}
                    />
                    <MetricPill
                      value={warningDisplay}
                      label="Warnings"
                      variant="warning"
                      icon={IconDefinitions.triangleExclamation}
                      isEmpty={isEmpty}
                    />
                    <MetricPill
                      value={failedDisplay}
                      label="Alerts"
                      variant="danger"
                      icon={IconDefinitions.circleExclamation}
                      isEmpty={isEmpty}
                    />
                  </div>
                </div>
                {hasFlwCounts && (
                  <div>
                    <div className="section-label">
                      Rolling Avg Deviation Breakdown
                    </div>

                    <BreakdownBar
                      passed={inspection.flwPassedCount ?? 0}
                      warning={inspection.flwWarningCount ?? 0}
                      failed={inspection.flwAlertCount ?? 0}
                      isEmpty={isFlwEmpty}
                    />

                    <div className="d-flex gap-2 mt-3">
                      <MetricPill
                        value={flwPassedDisplay}
                        label="Passed"
                        variant="success"
                        icon={IconDefinitions.circleCheck}
                        isEmpty={isFlwEmpty}
                      />
                      <MetricPill
                        value={flwWarningDisplay}
                        label="Warnings"
                        variant="warning"
                        icon={IconDefinitions.triangleExclamation}
                        isEmpty={isFlwEmpty}
                      />
                      <MetricPill
                        value={flwFailedDisplay}
                        label="Alerts"
                        variant="danger"
                        icon={IconDefinitions.circleExclamation}
                        isEmpty={isFlwEmpty}
                      />
                    </div>
                  </div>
                )}

                {/* Peak Gauges */}
                <div className="d-flex flex-column">
                  <div className="section-label">
                    {showFlwGauge ? "Peak Gauge Values" : "Peak Defect Rate"}
                  </div>
                  <div className="flex-fill d-flex flex-wrap align-items-start justify-content-center gap-3">
                    {gaugeConfig ? (
                      <>
                        <div className="flex-fill" style={{ minWidth: 180, maxWidth: 260 }}>
                          {showFlwGauge && (
                            <div
                              className="text-muted fw-semibold text-uppercase text-center mb-1"
                              style={{ fontSize: "0.65rem", letterSpacing: "0.04em" }}
                            >
                              Defect Rate
                            </div>
                          )}
                          <LiveGauge
                            peakFlawValue={inspection.peakFlawValue ?? 0}
                            config={gaugeConfig}
                          />
                        </div>
                        {showFlwGauge && (
                          <div className="flex-fill" style={{ minWidth: 180, maxWidth: 260 }}>
                            <div
                              className="text-muted fw-semibold text-uppercase text-center mb-1"
                              style={{ fontSize: "0.65rem", letterSpacing: "0.04em" }}
                            >
                              Rolling Avg Deviation
                            </div>
                            <LiveGauge
                              peakFlawValue={inspection.peakFlwDifference!}
                              config={flwConfig!}
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center text-muted py-3">
                        <small>No gauge settings configured</small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TitlelessCard>
          </div>
          <div className="col-lg-6 d-flex flex-column gap-4">
            {/* Session Details */}
            <TitlelessCard className="p-3">
              <div className="section-label">
                Inspection Details
              </div>
              <div>
                {inspection.inspectionDate && (
                  <DetailRow
                    iconName={IconDefinitions.clock}
                    label="Inspected"
                    value={new Date(inspection.inspectionDate).toLocaleString()}
                  />
                )}

                {inspection.tubing?.grade && (
                  <DetailRow
                    iconName={IconDefinitions.list}
                    label="Tubing Grade"
                    value={inspection.tubing.grade}
                  />
                )}

                {inspection.tubing?.size != null && (
                  <DetailRow
                    iconName={IconDefinitions.list}
                    label="Tubing Size"
                    value={inspection.tubing.size.toString()}
                  />
                )}

                {inspection.tubing?.length != null && (
                  <DetailRow
                    iconName={IconDefinitions.list}
                    label="Tubing Length"
                    value={inspection.tubing.length.toString()}
                  />
                )}

                {inspection.address && (
                  <DetailRow
                    iconName={IconDefinitions.company}
                    label="Location"
                    value={[
                      inspection.address.city,
                      inspection.address.province,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  />
                )}
              </div>
            </TitlelessCard>

            {/* Inspection Report */}
            <InspectionReportCard 
              inspection={inspection}
              inspectionId={inspectionId}
              onApprovalRequested={() =>
                setInspection((prev) =>
                  prev ? { ...prev, approvalRequested: true } : prev
                )
              }
            />
          </div>

          {/* Peak Signal Log */}
          {inspection.peakSignals != null && (
            <div className="col-12">
              <TitlelessCard className="p-3">
                <PeakSignalTable
                  peakSignals={inspection.peakSignals}
                  config={gaugeConfig}
                  canEdit={canEditPeaks}
                  onUpdate={handlePeakUpdate}
                  onDelete={handlePeakDelete}
                />
              </TitlelessCard>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CTEyeClientInspectionDetailsPage;
